// Designer F5.1 — client theme (design tokens). Module-level singleton so
// the canvas, inspector and theme editor share one loaded token map.
// Nodes reference tokens as "token:<key>"; resolve() turns that into the
// concrete colour (server render resolves the same way — see theme.ts).

import { ref, inject, getCurrentInstance } from 'vue';
import { callTool } from '../api/client';

export type ThemeTokens = Record<string, string>;
const TOKEN_PREFIX = 'token:';
import { documentThemeKey } from './canvas-host';
export { provideDocumentTheme } from './canvas-host';

const tokens = ref<ThemeTokens>({});
const overrides = ref<ThemeTokens>({});
const defaults = ref<ThemeTokens>({});
let loaded = false;
let inflight: Promise<void> | null = null;

async function load(force = false): Promise<void> {
  if (loaded && !force) return;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await callTool<{ tokens?: ThemeTokens; overrides?: ThemeTokens; defaults?: ThemeTokens }>('theme_get', {});
      tokens.value = r.tokens ?? {};
      overrides.value = r.overrides ?? {};
      defaults.value = r.defaults ?? {};
      loaded = true;
    } catch { /* theme is best-effort; nodes fall back to raw values */ }
    finally { inflight = null; }
  })();
  return inflight;
}

/** "token:color.crit" → its colour; raw values + unknown tokens pass through. */
function resolve(value: unknown): unknown {
  if (typeof value !== 'string' || !value.startsWith(TOKEN_PREFIX)) return value;
  const key = value.slice(TOKEN_PREFIX.length);
  return tokens.value[key] ?? defaults.value[key] ?? value;
}

function isToken(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(TOKEN_PREFIX);
}

async function save(next: ThemeTokens): Promise<void> {
  const r = await callTool<{ tokens?: ThemeTokens; overrides?: ThemeTokens }>('theme_set', { tokens: next });
  tokens.value = r.tokens ?? tokens.value;
  overrides.value = r.overrides ?? next;
  loaded = true;
}

/** Seed the token map directly (doc 34 F2 — the kiosk design view fetches
 *  tokens from a token-authed endpoint instead of callTool, then primes this
 *  singleton so the canvas resolves "token:*" without a user session). Marks it
 *  loaded so a later load() no-ops. */
function setTokens(next: ThemeTokens, defs?: ThemeTokens): void {
  tokens.value = next ?? {};
  if (defs) defaults.value = defs;
  loaded = true;
}

export function useTheme(documentTokens?: () => ThemeTokens | undefined) {
  const scoped = getCurrentInstance() ? inject(documentThemeKey, undefined) : undefined;
  const scopedResolve = (value: unknown) => typeof value === 'string' && value.startsWith(TOKEN_PREFIX)
    ? (documentTokens?.() ?? scoped?.value)?.[value.slice(TOKEN_PREFIX.length)] ?? resolve(value) : value;
  return { tokens, overrides, defaults, load, resolve: scopedResolve, isToken, save, setTokens, TOKEN_PREFIX };
}
